import { SearchParam } from "@hooks/useSearchParam";
import { z } from "zod";
import { logoutAnalyticsUser } from "./analytics/index.js";
import { AUTH_ORIGIN } from "./deployEnv.js";
import { StorageKey, storageKeyBuilders } from "./storageKeys.js";

const AUTH_TOKEN_KEY = StorageKey.AUTH_TOKEN;
const BASE64_URL_RANDOM_BYTES = 32;
const AUTHCHEMY_PKCE_STATE_TTL_MS = 10 * 60 * 1000;
const DASHBOARD_OAUTH_CLIENT_ID = "dashboard";
export const DASHBOARD_AUTHCHEMY_PKCE_CALLBACK_PATH = "/auth/callback";

/**
 * DASHBOARD AUTHCHEMY PKCE SECURITY RULES - DO NOT WEAKEN
 *
 * Initial requirement: reuse Authchemy's OAuth PKCE flow for dashboard and stop
 * exposing dashboard authToken values in URLs. The legacy authToken URL flow is
 * compatibility-only and must not be used for new dashboard login paths.
 *
 * Required invariants:
 * 1. Dashboard MUST send a cryptographically random state to Authchemy /oauth/authorize.
 * 2. Dashboard MUST store { codeVerifier, redirectUri, returnTo, createdAt } in
 *    tab-scoped sessionStorage before redirecting to Authchemy.
 * 3. Dashboard MUST process ?code=&state= only when the state key exists in
 *    sessionStorage and has not expired.
 * 4. Dashboard MUST send redirect_uri, code_verifier, and state directly to
 *    Authchemy /oauth/token from stored PKCE state, never from URL-provided
 *    verifier data.
 * 5. Dashboard MUST keep authToken out of redirect URLs except for the existing
 *    legacy compatibility fallback while old Authchemy/dashboard deployments overlap.
 */

const authchemyPkceStateSchema = z
  .object({
    codeVerifier: z.string().min(1),
    redirectUri: z.string().url(),
    returnTo: z.string().url(),
    createdAt: z.number(),
  })
  .refine(
    (state) => Date.now() - state.createdAt <= AUTHCHEMY_PKCE_STATE_TTL_MS,
  );

type AuthchemyPkceState = z.infer<typeof authchemyPkceStateSchema>;

const authchemyOAuthTokenResponseSchema = z.object({
  access_token: z.string().min(1),
});

export type AuthchemyPkceCallback =
  | { kind: "ready"; code: string; state: string }
  | { kind: "missingCode"; state: string }
  | { kind: "none" };

/**
 * Gets a stored Auth Token for local storage.  We do this instead of cookies.
 */
export function getStoredAuthToken(): string | undefined {
  return localStorage.getItem(AUTH_TOKEN_KEY) || undefined;
}

/**
 * Sets an auth token in local storage.
 */
export function setStoredAuthToken(authToken: string) {
  localStorage.setItem(AUTH_TOKEN_KEY, authToken);
}

/**
 * Removes the auth token from local storage.
 */
export function removeStoredAuthToken() {
  localStorage.removeItem(AUTH_TOKEN_KEY);
}

function base64UrlEncode(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

function createRandomBase64UrlString(): string {
  const bytes = new Uint8Array(BASE64_URL_RANDOM_BYTES);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

async function createCodeChallenge(codeVerifier: string): Promise<string> {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(codeVerifier),
  );
  return base64UrlEncode(new Uint8Array(digest));
}

function storeAuthchemyPkceState(state: string, value: AuthchemyPkceState) {
  sessionStorage.setItem(
    storageKeyBuilders.authchemyPkceState(state),
    JSON.stringify(value),
  );
}

export function getAuthchemyPkceCallback(
  search: string,
): AuthchemyPkceCallback {
  const searchParams = new URLSearchParams(search);
  const codes = searchParams.getAll(SearchParam.AuthCode);
  const states = searchParams.getAll(SearchParam.AuthState);

  if (codes.length > 1 || states.length > 1) {
    return { kind: "none" };
  }

  const code = codes[0];
  const state = states[0];

  if (code && state && getAuthchemyPkceState(state)) {
    return { kind: "ready", code, state };
  }

  if (!code && state && getAuthchemyPkceState(state)) {
    return { kind: "missingCode", state };
  }

  return { kind: "none" };
}

export function getAuthchemyPkceState(
  state: string,
): AuthchemyPkceState | undefined {
  return parseAuthchemyPkceState(
    sessionStorage.getItem(storageKeyBuilders.authchemyPkceState(state)),
  );
}

export function getAuthchemyPkceReturnTo(state: string): string | undefined {
  return getAuthchemyPkceState(state)?.returnTo;
}

export function removeAuthchemyPkceState(state: string) {
  sessionStorage.removeItem(storageKeyBuilders.authchemyPkceState(state));
}

function parseAuthchemyPkceState(
  rawValue: string | null,
): AuthchemyPkceState | undefined {
  if (!rawValue) {
    return undefined;
  }
  try {
    return authchemyPkceStateSchema.parse(JSON.parse(rawValue));
  } catch {
    return undefined;
  }
}

/**
 * URL for sending the user to authchemy to get authenticated, then come back.
 */
export function authchemyLoginRedirectUrl(redirectUrl: string) {
  return `${AUTH_ORIGIN}/login?redirectUrl=${encodeURIComponent(redirectUrl)}`;
}

/**
 * URL for sending the user to authchemy to create an account, forwarding query params
 * so that we can measure referral info such as utm_source, utm_medium, utm_campaign, etc.
 */
export function authchemySignupRedirectUrl(redirectUrl: string) {
  return `${AUTH_ORIGIN}?redirectUrl=${encodeURIComponent(redirectUrl)}`;
}

export async function authchemyPkceRedirectUrl({
  redirectUrl,
  redirectToLogin,
}: {
  redirectUrl: string;
  redirectToLogin: boolean;
}): Promise<string> {
  const codeVerifier = createRandomBase64UrlString();
  const codeChallenge = await createCodeChallenge(codeVerifier);
  const state = createRandomBase64UrlString();
  const callbackUrl = new URL(
    DASHBOARD_AUTHCHEMY_PKCE_CALLBACK_PATH,
    globalThis.location.origin,
  ).toString();
  storeAuthchemyPkceState(state, {
    codeVerifier,
    redirectUri: callbackUrl,
    returnTo: redirectUrl,
    createdAt: Date.now(),
  });

  const url = new URL(`${AUTH_ORIGIN}/oauth/authorize`);
  url.searchParams.set("response_type", "code");
  url.searchParams.set("client_id", "dashboard");
  url.searchParams.set("redirect_uri", callbackUrl);
  url.searchParams.set("code_challenge", codeChallenge);
  url.searchParams.set("code_challenge_method", "S256");
  url.searchParams.set("state", state);
  if (!redirectToLogin) {
    url.searchParams.set("screen_hint", "signup");
  }
  return url.toString();
}

export async function exchangeAuthchemyPkceCode({
  code,
  redirectUri,
  codeVerifier,
  state,
}: {
  code: string;
  redirectUri: string;
  codeVerifier: string;
  state: string;
}): Promise<{ authToken: string }> {
  const body = new URLSearchParams({
    grant_type: "authorization_code",
    client_id: DASHBOARD_OAUTH_CLIENT_ID,
    code,
    redirect_uri: redirectUri,
    code_verifier: codeVerifier,
    state,
  });

  const response = await fetch(`${AUTH_ORIGIN}/oauth/token`, {
    body,
    credentials: "include",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
    method: "POST",
  });

  if (!response.ok) {
    throw new Error("Failed to exchange Authchemy authorization code.");
  }

  const tokenResponse = authchemyOAuthTokenResponseSchema.parse(
    await response.json(),
  );
  return {
    authToken: tokenResponse.access_token,
  };
}

export function createDashboardAuthRedirectUrl(
  currentUrl: string,
  attempt?: number,
): string {
  const url = new URL(currentUrl);
  url.searchParams.delete(SearchParam.AuthCode);
  url.searchParams.delete(SearchParam.AuthState);
  url.searchParams.delete(SearchParam.AuthToken);
  url.searchParams.delete(SearchParam.RedirectAttempt);

  if (attempt) {
    url.searchParams.set(SearchParam.RedirectAttempt, `${attempt}`);
  }

  // flag for HomePage to check if we're coming from authchemy
  url.searchParams.set("appRedir", "1");

  return url.toString();
}

/**
 * Redirects to authchemy for authentication, returning to the same URL
 */
export async function redirectToAuthchemy(
  attempt?: number,
  redirectToLogin = false,
) {
  window.location.href = await authchemyPkceRedirectUrl({
    redirectUrl: createDashboardAuthRedirectUrl(window.location.href, attempt),
    redirectToLogin,
  });
}

/**
 * Logs out the user, and redirects back to this app's homepage if they log in again.
 */
export function logOutFromAuthchemy() {
  removeStoredAuthToken();
  logoutAnalyticsUser();

  const redirectUrl = new URL(globalThis.location.origin);
  redirectUrl.searchParams.set("appRedir", "1");
  const url = `${AUTH_ORIGIN}/logout?redirectUrl=${encodeURIComponent(
    redirectUrl.toString(),
  )}`;
  globalThis.location.href = url;
}
